// =============================================================================
// Discount catalogue (Revenue > Discounts & Trade Spend)
// The gross-to-net "give-away" build-up. Each group is a collapsible column
// group (same UX as the landed-cost stages on Stocked Pricing); collapsed, a
// group shows its total R spend contribution, expanded it shows each type.
//
// Every type declares a `basis`:
//   'pct' -> a percentage of gross sales   (spend = gross × value / 100)
//   'amt' -> a flat monetary amount        (spend = value)
//
// `type.code` MUST match the backend allow-list (DISCOUNT_TYPES in
// discountController.js) and is stored verbatim in bud_discounts.discount_code.
// `hiddenByDefault` groups (Mining) start hidden — users reveal them per the
// "Groups (shown)" toggles, exactly like the hideable stages.
// =============================================================================

export const DISCOUNT_GROUPS = [
  {
    code: 'g1',
    label: 'On-Invoice Discounts',
    short: 'On-Invoice',
    types: [
      { code: 'volume_qty',     label: 'Volume / Quantity',            basis: 'pct' },
      { code: 'trade_channel',  label: 'Trade / Channel',              basis: 'pct' },
      { code: 'promo_feature',  label: 'Promotional / Feature Price',  basis: 'pct' },
      { code: 'assortment_mix', label: 'Assortment / Mix',             basis: 'pct' },
      { code: 'new_line',       label: 'New-Line / Introductory',      basis: 'pct' },
    ],
  },
  {
    code: 'g2',
    label: 'Off-Invoice Trade Spend',
    short: 'Trade Spend',
    types: [
      { code: 'trade_promo',       label: 'Trade Promotions',          basis: 'pct' },
      { code: 'coop_ad',           label: 'Co-op Advertising',         basis: 'pct' },
      { code: 'listing_slotting',  label: 'Listing / Slotting Fees',   basis: 'amt' },
      { code: 'shelf_display',     label: 'Shelf / Gondola-end Fees',  basis: 'amt' },
      { code: 'scan_back',         label: 'Scan-back / Price Support',  basis: 'pct' },
      { code: 'catalogue_leaflet', label: 'Catalogue / Leaflet',       basis: 'amt' },
    ],
  },
  {
    code: 'g3',
    label: 'Rebates & Incentives',
    short: 'Rebates',
    types: [
      { code: 'volume_rebate',      label: 'Volume Rebate (tiered)',    basis: 'pct' },
      { code: 'growth_target',      label: 'Growth / Target Rebate',    basis: 'pct' },
      { code: 'loyalty_retro',      label: 'Loyalty / Retro Rebate',    basis: 'pct' },
      { code: 'distribution_range', label: 'Distribution / Range Bonus', basis: 'pct' },
    ],
  },
  {
    code: 'g4',
    label: 'Financial & Settlement',
    short: 'Financial',
    types: [
      { code: 'settlement',    label: 'Settlement / Prompt-Payment', basis: 'pct' },
      { code: 'early_payment', label: 'Early-Payment / Financing',   basis: 'pct' },
      { code: 'bad_debt',      label: 'Bad-debt / Credit-risk',      basis: 'pct' },
    ],
  },
  {
    code: 'g5',
    label: 'Logistics & Supply-Chain Allowances',
    short: 'Logistics',
    types: [
      { code: 'freight_delivery',  label: 'Freight / Delivery',           basis: 'amt' },
      { code: 'backhaul',          label: 'Backhaul / Collection',        basis: 'amt' },
      { code: 'central_dc',        label: 'Central DC / Warehouse',       basis: 'amt' },
      { code: 'returns_damages',   label: 'Returns / Damages / Spoilage', basis: 'pct' },
      { code: 'fill_rate_penalty', label: 'Fill-rate / Service Penalty',  basis: 'amt' },
    ],
  },
  {
    code: 'g6',
    label: 'Commodity / Mining Adjustments',
    short: 'Mining',
    hiddenByDefault: true,
    types: [
      { code: 'grade_penalty',  label: 'Grade / Quality Penalty',       basis: 'pct' },
      { code: 'grade_premium',  label: 'Grade / Quality Premium',       basis: 'pct' },
      { code: 'provisional_qp', label: 'Provisional Pricing (QP)',      basis: 'pct' },
      { code: 'spot_contract',  label: 'Spot-vs-Contract Differential', basis: 'pct' },
      { code: 'fob_cif',        label: 'FOB/CIF Freight Adjustment',    basis: 'amt' },
      { code: 'offtake',        label: 'Offtake / Long-term Contract',  basis: 'pct' },
    ],
  },
];

// Flat list of every discount type, in display order, carrying its group code.
export const ALL_DISCOUNT_TYPES = DISCOUNT_GROUPS.flatMap((g) =>
  g.types.map((t) => ({ ...t, group: g.code }))
);

// code -> type (with group + basis), for O(1) lookups.
export const DISCOUNT_BY_CODE = Object.fromEntries(ALL_DISCOUNT_TYPES.map((t) => [t.code, t]));

export const basisOf = (code) => DISCOUNT_BY_CODE[code]?.basis || 'pct';

// group code -> group, for default lookups.
const GROUP_BY_CODE = Object.fromEntries(DISCOUNT_GROUPS.map((g) => [g.code, g]));

// Default enabled state for a type when md_discount_config has no row for it:
// everything on, except types in a `hiddenByDefault` group (Mining). The
// discount-config table stores only deviations from this.
export const defaultEnabled = (code) => {
  const t = DISCOUNT_BY_CODE[code];
  if (!t) return false;
  return !GROUP_BY_CODE[t.group]?.hiddenByDefault;
};

// Resolve enabled state from a { code: is_enabled } config map, falling back to
// the default when the code is absent.
export const isEnabled = (configMap, code) =>
  Object.prototype.hasOwnProperty.call(configMap || {}, code) ? !!configMap[code] : defaultEnabled(code);

export default DISCOUNT_GROUPS;
